/* eslint-disable @typescript-eslint/no-unsafe-assignment,
                   @typescript-eslint/no-unsafe-return,
                   @typescript-eslint/explicit-module-boundary-types,
                   @typescript-eslint/array-type,
                   sonarjs/no-duplicate-string,
                   sonarjs/cognitive-complexity,
                   complexity,
                   react-hooks/exhaustive-deps */
// This file imports from `.js` siblings (rfpReducer.js, utils.js). Because
// the broker-portal-fe tsconfig has `allowJs: false`, typescript-eslint
// resolves those imports as error-typed, cascading unsafe-* errors across
// this file. The fix is to migrate those `.js` files to TypeScript; until
// that's scheduled, suppress at the file level so unrelated changes here
// aren't blocked by pre-existing lint debt. See: TODO(tech-debt).
import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
  useRef,
  ReactNode,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import axios from "axios";
import { logger } from "@platform/browser-logger";
import { useApi } from "@platform/api";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  EN_CENSUS_FEATURE_ENABLED as EN_CENSUS_FEATURE_FLAG,
  EN_CENSUS_FOR_PROSPECTS as EN_CENSUS_FOR_PROSPECTS_FLAG,
  EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED as EN_PUSH_SOLD_PLAN_DATA_FEATURE_FLAG,
} from "@platform/feature-flags";
import {
  CLIENT_ACTION_VALUES,
  CURRENT_COVERAGES,
  RFP_ACTION_VALUES,
} from "@platform/rfp-core";

import { ROUTE_NAMES } from "../../../constants";
import RfpReducer, {
  isCoverageInvalid,
  initState as initRfpState,
} from "../reducers/rfpReducer";
import { useToastNotificationService } from "../../../hooks";
import {
  EN_CENSUS_AVAILABLE_VALUES,
  EN_PUSH_SOLD_PLAN_DATA_VALUES,
  CLIENT_TYPES,
  RFP_ACTION_TYPES,
  RFP_ORIGIN_FLOW,
  VICEVERSA_RFP_STEPS,
} from "../constants";
import {
  filterProductDataBasedOnFlag,
  formatDate,
  getJsonBlob,
  extractErrorMessage,
} from "../../../utils";
import { Loader } from "../../../components";
import {
  getStepLabel,
  getTimeFromLastUpdate,
  buildDocumentTypes,
  normalizeDentalCoverages,
  migrateStdFicaPlans,
  mergeAdditionalDocumentNames,
} from "../utils";
import { submitEvent } from "../../../utils/tracking";
import { deriveCommissionText } from "../deriveCommissionText";
import RfpLoader from "../components/rfpLoader";

import type { RfpContextType, RfpDocument } from "./types";

const RfpContext = createContext<RfpContextType | undefined>(undefined);

const {
  IS_CURRENT_CLIENT,
  CLIENT_TYPE,
  CLIENT_ID,
  CLIENT_SINCE,
  NAME,
  EFFECTIVE_DATE,
  DUE_DATE,
  TOTAL_BENEFITS,
  STATE,
  SITUS_STATE,
  HAS_BEN_ADMIN_SYSTEM_STATUS,
  REQUESTED_SYSTEM,
  REQUESTED_SYSTEM_OTHER,
  TECH_IMPLEMENTATION,
  TECH_CREDIT_TYPE,
  TECH_CREDIT_AMOUNT,
  MARKETING_OBJECTIVES,
  REQUESTED_AMOUNT,
  FORM_INFO,
  CLIENT_ERROR,
  EN_CENSUS_AVAILABLE,
  EN_CENSUS_FEATURE_ENABLED,
  EN_CENSUS_FOR_PROSPECTS,
  EN_PUSH_SOLD_PLAN_DATA,
  EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED,
} = CLIENT_ACTION_VALUES;

const CENSUS_AVAILABLE_YES = EN_CENSUS_AVAILABLE_VALUES.YES;
const CENSUS_AVAILABLE_NO = EN_CENSUS_AVAILABLE_VALUES.NO;
const PUSH_SOLD_PLAN_DATA_YES = EN_PUSH_SOLD_PLAN_DATA_VALUES.YES;
const PUSH_SOLD_PLAN_DATA_NO = EN_PUSH_SOLD_PLAN_DATA_VALUES.NO;

const normalizeCensusAvailable = (value: unknown): string => {
  if (value === true || value === CENSUS_AVAILABLE_YES) {
    return CENSUS_AVAILABLE_YES;
  }

  if (value === false || value === CENSUS_AVAILABLE_NO) {
    return CENSUS_AVAILABLE_NO;
  }

  return "";
};

const normalizePushSoldPlanData = (value: unknown): string => {
  if (value === true || value === PUSH_SOLD_PLAN_DATA_YES) {
    return PUSH_SOLD_PLAN_DATA_YES;
  }

  if (value === false || value === PUSH_SOLD_PLAN_DATA_NO) {
    return PUSH_SOLD_PLAN_DATA_NO;
  }

  return "";
};

const normalizeHasBenAdminSystemStatus = (value: unknown): string => {
  if (value === true) {
    return "1";
  }

  if (value === false) {
    return "0";
  }

  if (value === "yes" || value === "1") {
    return "1";
  }

  if (value === "no" || value === "0") {
    return "0";
  }

  if (value === "unknown") {
    return value;
  }

  return "1";
};

const toApiHasBenAdminSystemStatus = (value: unknown): string | null => {
  if (value === "1" || value === "yes") {
    return "yes";
  }

  if (value === "0" || value === "no") {
    return "no";
  }

  if (value === "unknown") {
    return "unknown";
  }

  return null;
};

const shouldIncludeCensusAvailable = (
  formInfo: Record<string, unknown>,
): boolean =>
  formInfo[HAS_BEN_ADMIN_SYSTEM_STATUS] === "1" &&
  formInfo[REQUESTED_SYSTEM] === "employeeNavigator" &&
  !!formInfo[EN_CENSUS_AVAILABLE] &&
  (formInfo[CLIENT_TYPE] === CLIENT_TYPES.CURRENT_CLIENT ||
    formInfo[EN_CENSUS_FOR_PROSPECTS] === true);

const shouldIncludePushSoldPlanData = (
  formInfo: Record<string, unknown>,
): boolean =>
  formInfo[EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED] === true &&
  formInfo[HAS_BEN_ADMIN_SYSTEM_STATUS] === "1" &&
  formInfo[REQUESTED_SYSTEM] === "employeeNavigator" &&
  !!formInfo[EN_PUSH_SOLD_PLAN_DATA] &&
  (formInfo[CLIENT_TYPE] === CLIENT_TYPES.CURRENT_CLIENT ||
    formInfo[EN_CENSUS_FOR_PROSPECTS] === true);

const {
  PREPARE_RFP,
  UPDATE_LAST_MODIFIED,
  UPDATE_RFP_ID,
  CLEANUP_DOCUMENTS,
  UPDATE_STATE_UPLOAD_DOCUMENTS_WITHOUT_COVERAGE,
  UPDATE_STATE_DELETE_DOCUMENT,
} = RFP_ACTION_TYPES;
const {
  SELECTED_CARRIERS,
  IS_CENTRO_CARRIER_SELECTED,
  SELECTED_COVERAGES,
  ARE_COVERAGES_VALID,
  DOCUMENTS,
  ACTIVE_STEP,
  HIGHER_LAST_STEP,
  ID,
  LAST_MODIFIED,
  DOCUMENT_TYPES,
  ARE_DOCUMENTS_VALID,
  UPLOADED_FILES,
  ADDITIONAL_DOCUMENTS,
} = RFP_ACTION_VALUES;

interface RfpProviderProps {
  children: ReactNode;
}

function RfpProvider({ children }: RfpProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rfp, setRfp] = useReducer(RfpReducer, initRfpState);
  const { createToastNotification } = useToastNotificationService();
  const [enableWarningNavigation, setEnableWarningNavigation] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [isSavingDraftRfp, setIsSavingDraftRfp] = useState(false);
  const [isSubmittingRfp, setIsSubmittingRfp] = useState(false);
  const [fetchingRfpError, setFetchingRfpError] = useState<string | null>(null);
  const [discardDraftError, setDiscardDraftError] = useState<string | null>(
    null,
  );
  const [originRfp, setOriginRfp] = useState<string | null>(null);
  const [submittingError, setSubmittingError] = useState<string | null>(null);
  const axiosInstance = useApi();
  const enableEnCensus = useFeatureIsOn(EN_CENSUS_FEATURE_FLAG);
  const enableEnCensusForProspects = useFeatureIsOn(
    EN_CENSUS_FOR_PROSPECTS_FLAG,
  );
  const enablePushSoldPlanData = useFeatureIsOn(
    EN_PUSH_SOLD_PLAN_DATA_FEATURE_FLAG,
  );
  const [isDocumentsValid, setIsDocumentsValid] = useState(false);
  const [isRFPDataLoaded, setIsRFPDataLoaded] = useState(false);

  const draftRfpId = useRef<number | null>(null);
  const draftRfpUuid = useRef<string | null>(null);

  const fetchRfpData = async (bodyPost: {
    rfpId?: number;
    clientId?: string;
    draftUuid?: string;
  }) => {
    setLoader(true);
    try {
      const { data } = await axiosInstance.post(
        "/brokerportal/v1/prepareRfp",
        bodyPost,
      );

      let responseData: Record<string, unknown>;
      if (data) {
        responseData = filterProductDataBasedOnFlag(data);
      }

      const draftData = (responseData.draft as Record<string, unknown>) ?? null;
      if (draftData) {
        const draft = draftData?.draft as Record<string, unknown>;
        const draftRawForm = draft?.rawForm as Record<string, unknown>;
        const draftClientInfo = responseData?.clientDetail as Record<
          string,
          unknown
        >;
        const draftClientType = `${(draftRawForm.clientType as string) || ""}_client`;
        const isCurrentClient = draftClientType === CLIENT_TYPES.CURRENT_CLIENT;
        const draftAdditionalInfo = draftRawForm?.additionalInfo as Record<
          string,
          unknown
        >;
        const benAdminMetadata = draftAdditionalInfo?.["benAdminMetadata"] as
          | {
              hasBenAdminSystemStatus?: boolean | string;
              censusAvailable?: boolean | string;
              pushSoldPlanData?: boolean | string;
            }
          | undefined;
        const requestedSystemMetadata = draftAdditionalInfo?.[
          "requestedSystemMetadata"
        ] as { censusAvailable?: boolean | string } | undefined;
        const draftState = draftClientInfo?.state
          ? (responseData.states as Array<{ name: string }>).find(
              (st) => st.name === draftClientInfo.state,
            )
          : "";
        const draftSitusState = draftClientInfo?.situsState
          ? (responseData.states as Array<{ name: string }>).find(
              (st) => st.name === draftClientInfo.situsState,
            )
          : "";
        const clientName =
          draftClientInfo?.clientName || draftClientInfo?.name || "";
        const formInfo = {
          ...draftClientInfo,
          externalId: (draftData as { uuid?: string })?.uuid,
          brokerId: (draftData as { brokerId?: string })?.brokerId,
          [CLIENT_ID]: (draft?.metadata as Record<string, unknown>)?.clientId,
          [STATE]: draftState,
          [SITUS_STATE]: draftSitusState,
          [NAME]: clientName,
          [DUE_DATE]: draftRawForm?.dueDate,
          [EFFECTIVE_DATE]: draftRawForm?.effectiveDate,
          [TOTAL_BENEFITS]: draftClientInfo?.numberOfEmployees,
          [CLIENT_SINCE]: draftClientInfo?.clientSince,
          [HAS_BEN_ADMIN_SYSTEM_STATUS]: normalizeHasBenAdminSystemStatus(
            benAdminMetadata?.hasBenAdminSystemStatus ??
              draftAdditionalInfo?.hasBenAdminSystemStatus ??
              draftAdditionalInfo?.requestedSystemInPlace,
          ),
          [REQUESTED_SYSTEM]: draftAdditionalInfo?.requestedSystem || "",
          [REQUESTED_SYSTEM_OTHER]:
            draftAdditionalInfo?.requestedSystemOther || "",
          [TECH_IMPLEMENTATION]: draftAdditionalInfo?.technologyCreditNeeded
            ? "1"
            : "0",
          [TECH_CREDIT_TYPE]: draftAdditionalInfo?.techCreditType || "",
          [TECH_CREDIT_AMOUNT]: draftAdditionalInfo?.techCreditAmount || null,
          [MARKETING_OBJECTIVES]: draftAdditionalInfo?.marketingObjectives,
          [REQUESTED_AMOUNT]: draftAdditionalInfo?.requestedAmount,
          [EN_CENSUS_FEATURE_ENABLED]: enableEnCensus,
          [EN_CENSUS_FOR_PROSPECTS]: enableEnCensusForProspects,
          [EN_CENSUS_AVAILABLE]: normalizeCensusAvailable(
            benAdminMetadata?.censusAvailable ??
              requestedSystemMetadata?.censusAvailable,
          ),
          [EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED]: enablePushSoldPlanData,
          [EN_PUSH_SOLD_PLAN_DATA]: normalizePushSoldPlanData(
            benAdminMetadata?.pushSoldPlanData,
          ),
          [CLIENT_TYPE]: draftClientType,
          [IS_CURRENT_CLIENT]: isCurrentClient,
        };

        const currentCoverages = responseData[CURRENT_COVERAGES] as Array<{
          id: string;
        }>;
        const normalizedCoverages = normalizeDentalCoverages(
          draftRawForm.coverages as Array<Record<string, unknown>>,
          responseData.product as Array<Record<string, unknown>>,
        ).map(migrateStdFicaPlans);
        const allCoverages = normalizedCoverages
          .map((c: Record<string, unknown>) => {
            if (currentCoverages?.some((cc) => cc.id === c.id)) {
              return { ...c, isCurrentCoverage: true };
            }
            return c;
          })
          .map((c: Record<string, unknown>) => ({
            ...c,
            isValid: !isCoverageInvalid(c),
          }));
        let activeStep = VICEVERSA_RFP_STEPS[draft?.currentStep as string] ?? 0;

        const { documentTypes, uploadedFiles } = buildDocumentTypes(
          allCoverages,
          isCurrentClient,
          responseData.rfpFiles,
          !!draftData?.draft,
        );

        const areDocumentsValid = documentTypes.every(
          (doc: Record<string, { required: Array<{ files: unknown[] }> }>) =>
            doc.coverages.required.every((item) => item.files.length > 0),
        );

        const isCentroCarrierEnabled =
          draftClientInfo?.centro_select_carrier_enabled;
        const carriers = draftRawForm.carriers as unknown[];
        if (
          !carriers.length &&
          (!isCentroCarrierEnabled ||
            !draftRawForm[IS_CENTRO_CARRIER_SELECTED]) &&
          (activeStep === VICEVERSA_RFP_STEPS.Documents ||
            activeStep === VICEVERSA_RFP_STEPS.Review)
        ) {
          activeStep = VICEVERSA_RFP_STEPS.Carriers;
        }
        setRfp({
          type: PREPARE_RFP,
          value: {
            ...responseData,
            ...draftData,
            [SELECTED_COVERAGES]: allCoverages,
            [FORM_INFO]: formInfo,
            [IS_CURRENT_CLIENT]: isCurrentClient,
            [HIGHER_LAST_STEP]: activeStep,
            [ACTIVE_STEP]: activeStep,
            [ARE_COVERAGES_VALID]: allCoverages.every((c) => c.isValid),
            [SELECTED_CARRIERS]: draftRawForm.carriers,
            [IS_CENTRO_CARRIER_SELECTED]: isCentroCarrierEnabled
              ? (draftRawForm[IS_CENTRO_CARRIER_SELECTED] ?? false)
              : false,
            [DOCUMENT_TYPES]: documentTypes,
            [ARE_DOCUMENTS_VALID]: areDocumentsValid,
            [UPLOADED_FILES]: uploadedFiles,
            [ADDITIONAL_DOCUMENTS]: mergeAdditionalDocumentNames(
              draftRawForm?.additionalDocuments,
              responseData.rfpFiles,
            ),
          },
        });
      } else {
        const locationState = location.state as Record<string, unknown> | null;
        const client = locationState?.client as
          | Record<string, unknown>
          | undefined;
        const clientType = client?.clientType as string;
        const isCurrentClient = clientType === CLIENT_TYPES.CURRENT_CLIENT;
        setRfp({
          type: PREPARE_RFP,
          value: {
            ...responseData,
            ...locationState,
            [FORM_INFO]: {
              ...client,
              [CLIENT_TYPE]: client ? client[CLIENT_TYPE] : locationState?.type,
              [IS_CURRENT_CLIENT]: isCurrentClient,
              [EN_CENSUS_FEATURE_ENABLED]: enableEnCensus,
              [EN_CENSUS_FOR_PROSPECTS]: enableEnCensusForProspects,
              [EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED]: enablePushSoldPlanData,
            },
            [IS_CURRENT_CLIENT]: isCurrentClient,
            [CURRENT_COVERAGES]: responseData[CURRENT_COVERAGES] || [],
            [SELECTED_CARRIERS]: [],
            [IS_CENTRO_CARRIER_SELECTED]: false,
          },
        });
      }
      setEnableWarningNavigation(true);
      setLoader(false);
      setFetchingRfpError(null);
    } catch (e) {
      // When the user arrived via an SSO deep link (?draftId=<uuid>) and the
      // backend rejects the draft — not found, not a draft, or belongs to
      // another broker (all surfaced as 404) — bounce to client selection
      // instead of leaving the wizard in a broken error state. For any
      // other error (network, 5xx, etc.) fall through to the retryable
      // error path so transient failures don't boot the user.
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      const isDraftRejection =
        !!draftRfpUuid.current && (status === 404 || status === 403);

      if (isDraftRejection) {
        setLoader(false);
        setIsRFPDataLoaded(true);
        navigate(`/${ROUTE_NAMES.CLIENT_SELECTION}`, { replace: true });

        return;
      }

      // Surface the error to Datadog/console. Post-response transform
      // failures (TypeErrors, etc.) land here as well as genuine HTTP
      // failures; without this log they're silently displayed as the
      // generic "trouble loading" UI with no diagnostic trail. The
      // request-shape fields disambiguate which prepareRfp branch
      // (rfpId / draftUuid / clientId) was being invoked.
      logger.error("fetchRfpData failed", e, {
        rfpId: bodyPost.rfpId,
        draftUuid: bodyPost.draftUuid,
        clientId: bodyPost.clientId,
        status: axios.isAxiosError(e) ? e.response?.status : undefined,
      });

      setFetchingRfpError(
        extractErrorMessage(e as Error, "An unexpected error occurred"),
      );
      setLoader(false);
    } finally {
      setIsRFPDataLoaded(true);
    }
  };

  const dispatchCall = () => {
    const locationState = location.state as Record<string, unknown> | null;
    const client = locationState?.client as Record<string, unknown> | undefined;
    if (draftRfpId.current) {
      setOriginRfp(RFP_ORIGIN_FLOW.DRAFT);
      fetchRfpData({ rfpId: draftRfpId.current });
    } else if (draftRfpUuid.current) {
      setOriginRfp(RFP_ORIGIN_FLOW.DRAFT);
      fetchRfpData({ draftUuid: draftRfpUuid.current });
    } else if (client) {
      setOriginRfp(RFP_ORIGIN_FLOW.CLIENT);
      fetchRfpData({ clientId: client.clientId as string });
    } else {
      setOriginRfp(null);
      navigate(`/${ROUTE_NAMES.CLIENT_SELECTION}`, { replace: true });
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (draftRfpId.current) {
        sessionStorage.setItem("draftRfpId", draftRfpId.current.toString());
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const locationState = location.state as Record<string, unknown> | null;
    const reloadedDraftRfpId = sessionStorage.getItem("draftRfpId");
    const queryDraftUuid = searchParams.get("draftId");

    if (locationState?.draftRfpId) {
      draftRfpId.current = locationState.draftRfpId as number;
    } else if (reloadedDraftRfpId) {
      draftRfpId.current = parseInt(reloadedDraftRfpId);
    } else if (queryDraftUuid) {
      draftRfpUuid.current = queryDraftUuid;
    }

    sessionStorage.removeItem("draftRfpId");

    dispatchCall();
  }, []);

  const preparePayload = () => {
    const formInfo = rfp[FORM_INFO];
    const benAdminMetadata = {
      hasBenAdminSystemStatus: toApiHasBenAdminSystemStatus(
        formInfo[HAS_BEN_ADMIN_SYSTEM_STATUS],
      ),
      ...(shouldIncludeCensusAvailable(formInfo)
        ? { censusAvailable: formInfo[EN_CENSUS_AVAILABLE] }
        : {}),
      ...(shouldIncludePushSoldPlanData(formInfo)
        ? { pushSoldPlanData: formInfo[EN_PUSH_SOLD_PLAN_DATA] }
        : {}),
    };

    return {
      client: formInfo[CLIENT_ID],
      dueDate: formatDate(formInfo[DUE_DATE]),
      effectiveDate: formatDate(formInfo[EFFECTIVE_DATE]),
      carriers: rfp[SELECTED_CARRIERS],
      centroCarrierSelectionRequested: rfp[IS_CENTRO_CARRIER_SELECTED],
      coverages: deriveCommissionText(rfp[SELECTED_COVERAGES]),
      clientType: formInfo[CLIENT_TYPE]?.replace("_client", ""),
      additionalInfo: {
        benAdminMetadata,
        requestedSystem: formInfo[REQUESTED_SYSTEM] || null,
        requestedSystemOther: formInfo[REQUESTED_SYSTEM_OTHER] || null,
        technologyCreditNeeded: formInfo[TECH_IMPLEMENTATION] === "1",
        techCreditType: formInfo[TECH_CREDIT_TYPE] || null,
        techCreditAmount: formInfo[TECH_CREDIT_AMOUNT]
          ? parseFloat(formInfo[TECH_CREDIT_AMOUNT] as string)
          : null,
        marketingObjectives: formInfo[MARKETING_OBJECTIVES],
        requestedAmount: formInfo[REQUESTED_AMOUNT],
      },
      additionalDocuments: rfp[ADDITIONAL_DOCUMENTS],
    };
  };

  const buildRfpEventPayload = () => {
    const formInfo = rfp[FORM_INFO] as Record<string, unknown>;
    const coverages = (rfp[SELECTED_COVERAGES] as Array<{ name: string }>).map(
      (c) => c.name,
    );
    return {
      client_type: formInfo[CLIENT_TYPE],
      rfp_id: rfp[ID],
      rfp_carrier_names: rfp[SELECTED_CARRIERS],
      centroCarrierSelectionRequested: rfp[IS_CENTRO_CARRIER_SELECTED],
      client_size: formInfo[TOTAL_BENEFITS],
      rfp_coverage_type: coverages,
    };
  };

  const handleSubmitRFP = async (e: React.FormEvent) => {
    e.preventDefault();

    const eventPayload = buildRfpEventPayload();
    submitEvent("RFP Submitted", eventPayload);

    const payload = preparePayload() as Record<string, unknown>;
    payload.rfpId = rfp[ID];
    let submitError: string | undefined;
    const formData = new FormData();
    formData.append("rfp", getJsonBlob(payload));

    const uniqueFile: Record<string, boolean> = {};
    const documents = rfp[DOCUMENTS] as {
      files: Array<File & { name: string }>;
    };
    for (const file of documents.files) {
      if (!uniqueFile[file.name] && file instanceof File) {
        uniqueFile[file.name] = true;
        formData.append(file.name, file);
      }
    }

    setIsSubmittingRfp(true);
    try {
      const { data } = await axiosInstance.post(
        "/brokerportal/v1/submitRfp",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      await setEnableWarningNavigation(false);
      createToastNotification({
        severity: "success",
        message: "RFP successfully created",
      });
      submitEvent("RFP Successfully Submitted", eventPayload);

      navigate(`/${ROUTE_NAMES.RFPS}/${data.id}`);
    } catch (e) {
      logger.error("Submit RFP error", e);
      await setEnableWarningNavigation(false);
      const errorMsg = extractErrorMessage(
        e as Error,
        "Error received from backend services",
      );
      submitEvent("RFP Submission Error", {
        rfp_id: rfp[ID],
        error: errorMsg,
      });
      setSubmittingError(errorMsg);
      submitError = extractErrorMessage(e as Error);
    } finally {
      setIsSubmittingRfp(false);
      submitEvent("rfp-submit", {
        status: submitError ? "failed" : "successful",
        error: submitError,
      });
    }
  };

  const handleDiscardRfpDraft = async () => {
    setLoader(true);
    const rfpId = rfp[ID];
    try {
      if (rfpId != null) {
        await axiosInstance.post(`brokerportal/v1/deleteRfpDraft`, {
          rfpId,
        });
      }
      await setEnableWarningNavigation(false);
      createToastNotification({
        severity: "success",
        message: "RFP Draft discarded",
      });
      setDiscardDraftError(null);
      setIsRFPDataLoaded(false);
      navigate("/");
    } catch (err) {
      logger.error("Discard RFP draft error", err);
      await setEnableWarningNavigation(false);
      const errorMsg = extractErrorMessage(
        err as Error,
        `There was an error discarding RFP ${rfpId} Draft.`,
      );
      setDiscardDraftError(errorMsg);
    } finally {
      setLoader(false);
    }
  };

  const buildErrorText = () => {
    const lastModified = rfp[LAST_MODIFIED];
    const timeFromLastUpdate = getTimeFromLastUpdate(lastModified);
    if (timeFromLastUpdate) {
      return `Your RFP was last saved ${timeFromLastUpdate} ago. Please try again later.`;
    }
    return `Unable to save your RFP. Please try again later.`;
  };

  // Returns whether the document row was deleted. Callers that also drop the
  // document from the draft must not do so on a false return, or the draft and
  // the surviving row disagree.
  const deleteRFPDocument = async (
    fileToBeDeleted: RfpDocument,
  ): Promise<boolean> => {
    submitEvent("RFP Document Deleted", {
      rfp_id: rfp[ID],
      client_type: rfp[FORM_INFO][CLIENT_TYPE],
    });

    try {
      setIsDocumentUploading(true);

      const response = await axiosInstance.delete(
        `/brokerportal/v1/documents/${fileToBeDeleted.id}`,
      );

      if (response) {
        setRfp({
          type: UPDATE_STATE_DELETE_DOCUMENT,
          value: { deletedData: fileToBeDeleted },
        });

        const documents = rfp[DOCUMENTS] as {
          files: Array<{ filename?: string }>;
        };
        let fileObjects = [...documents.files];
        fileObjects = fileObjects.filter(
          (fileItem) => fileItem.filename !== fileToBeDeleted.name,
        );
        setRfp({
          type: CLEANUP_DOCUMENTS,
          value: fileObjects,
        });
      }

      return true;
    } catch (e) {
      setIsDocumentUploading(false);
      logger.error("Delete document error", e);
      createToastNotification({
        severity: "error",
        title: `${fileToBeDeleted.name} could not be deleted. Please try again or reach out to help@centrobenefits.com.`,
      });

      return false;
    }
  };

  const updateRFPDocument = async (
    filesToBeUpdated: Array<{ id: string | number; documentType: string[] }>,
    closeLoader = false,
  ) => {
    submitEvent("RFP Document Updated", {
      rfp_id: rfp[ID],
      client_type: rfp[FORM_INFO][CLIENT_TYPE],
    });

    try {
      setIsDocumentUploading(true);

      const patchData = (id: string | number, data: { types: string[] }) =>
        axiosInstance.patch(`/brokerportal/v1/documents/${id}`, data);

      const responses = await Promise.all(
        filesToBeUpdated.map((request) =>
          patchData(request.id, { types: request.documentType }),
        ),
      );

      if (responses && responses.length) {
        const responseData = responses.map((item) => item.data);

        setRfp({
          type: UPDATE_STATE_UPLOAD_DOCUMENTS_WITHOUT_COVERAGE,
          value: { uploadedData: responseData },
        });
        setRfp({
          type: UPDATE_LAST_MODIFIED,
          value: responseData?.[0]?.updated_at,
        });

        const documents = rfp[DOCUMENTS] as {
          files: Array<Record<string, unknown>>;
        };
        let fileObjects = [...documents.files];
        fileObjects = fileObjects.map((fileItem) => {
          const fileItemExist = responseData.find(
            (resFile: Record<string, unknown>) =>
              resFile.filename === fileItem.filename,
          );
          if (fileItemExist) {
            fileItem.types = fileItemExist.types;
          }
          return fileItem;
        });
        setRfp({
          type: CLEANUP_DOCUMENTS,
          value: fileObjects,
        });
      }

      if (closeLoader) {
        setIsDocumentUploading(false);
      }
    } catch (e) {
      setIsDocumentUploading(false);
      logger.error("Update document error", e);
      createToastNotification({
        severity: "error",
        title: `File could not be updated. Please try again or reach out to help@centrobenefits.com.`,
      });
    }
  };

  const uploadRFPDocument = async (
    _files: File[],
    documentTypeName: string,
  ) => {
    submitEvent("RFP Document Upload", {
      rfp_id: rfp[ID],
      client_type: rfp[FORM_INFO][CLIENT_TYPE],
    });
    const uniqueFileNames = new Set<string>();

    try {
      setIsDocumentUploading(true);
      const formData = new FormData();
      formData.append("documentType", documentTypeName);
      const documents = rfp[DOCUMENTS] as {
        files: Array<File | Record<string, unknown>>;
      };
      let fileObjects = [...documents.files];

      for (const file of _files) {
        if (!uniqueFileNames.has(file.name)) {
          uniqueFileNames.add(file.name);
          if (file instanceof File) {
            formData.append("files[]", file);
          }
        }
      }

      const response = await axiosInstance.post(
        `/brokerportal/v1/rfp/${rfp[ID]}/documents`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      setIsDocumentUploading(false);

      draftRfpId.current = response.data?.[0]?.rfpId;

      setRfp({
        type: UPDATE_LAST_MODIFIED,
        value: response.data?.[0]?.updated_at,
      });

      setRfp({
        type: UPDATE_STATE_UPLOAD_DOCUMENTS_WITHOUT_COVERAGE,
        value: { uploadedData: response.data },
      });

      if (response?.data?.length) {
        const responseData = response.data as Array<{
          types: string[];
          filename: string;
          url: string;
          infected: boolean;
        }>;
        const newFileData = responseData.reduce(
          (acc: Array<Record<string, unknown>>, item) => {
            if (item.types.length) {
              const multipleTypeData = item.types.map((typeItem) => ({
                ...item,
                name: item.filename,
                downloadLink: item.url,
                infected: false,
                type: typeItem,
              }));

              return [...acc, ...multipleTypeData];
            }
            return acc;
          },
          [],
        );
        fileObjects = [...fileObjects, ...newFileData];
      }
      setRfp({
        type: CLEANUP_DOCUMENTS,
        value: fileObjects,
      });

      return response;
    } catch (e) {
      setIsDocumentUploading(false);
      logger.error("Upload document error", e);
      const fileNamesString = Array.from(uniqueFileNames).join(", ");
      createToastNotification({
        severity: "error",
        title: `${fileNamesString} could not be uploaded. Please try again or reach out to help@centrobenefits.com.`,
      });
      return e;
    }
  };

  const handleSaveDraft = async (
    isLeavingRfp = false,
    isUpdatingRfp = false,
  ) => {
    let submitError: string | undefined;
    setIsSavingDraftRfp(true);
    submitEvent("RFP Draft Saved", {
      rfp_id: rfp[ID],
      client_type: rfp[FORM_INFO][CLIENT_TYPE],
    });

    try {
      const formInfo = rfp[FORM_INFO];
      const payload = preparePayload();
      const additionalDocuments = rfp[ADDITIONAL_DOCUMENTS] || [];
      const finalPayload = {
        rfpId: rfp[ID],
        rawForm: payload,
        currentStep: getStepLabel(rfp[ACTIVE_STEP]),
        clientName: formInfo[NAME],
        clientId: formInfo[CLIENT_ID],
      };

      const formData = new FormData();
      formData.append("rfp", getJsonBlob(finalPayload));

      const fileObjects: Array<File & { name: string }> = [];
      const uniqueFileNames = new Set<string>();
      const documents = rfp[DOCUMENTS] as {
        files: Array<File & { name: string }>;
      };

      for (const file of documents.files) {
        if (!uniqueFileNames.has(file.name)) {
          uniqueFileNames.add(file.name);
          if (additionalDocuments.includes(file.name) && file instanceof File) {
            formData.append(file.name, file);
          }
          fileObjects.push(file);
        }
      }

      const response = await axiosInstance.post(
        "/brokerportal/v1/saveRfpDraft",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      setRfp({
        type: UPDATE_RFP_ID,
        value: response.data?.rfpId,
      });

      draftRfpId.current = response.data?.rfpId;

      setRfp({
        type: UPDATE_LAST_MODIFIED,
        value: response.data?.lastModifyDate,
      });

      if (isUpdatingRfp) {
        setIsDocumentUploading(false);
      }
      if (isLeavingRfp) {
        createToastNotification({
          severity: "success",
          message: "RFP draft successfully saved",
        });
      }
    } catch (e) {
      logger.error("Save RFP draft error", e);
      createToastNotification({
        severity: "error",
        message: buildErrorText(),
        title: "Couldn't save your RFP",
      });
      submitError = extractErrorMessage(e as Error);
    } finally {
      submitEvent("rfp-draft-submit", {
        status: submitError ? "failed" : "successful",
        error: submitError,
      });
      setIsSavingDraftRfp(false);
    }
  };

  const handleCloseSubmitError = () => setSubmittingError(null);

  if (loader || !isRFPDataLoaded) return <Loader />;

  if (isSubmittingRfp) return <RfpLoader />;

  return (
    <RfpContext.Provider
      value={{
        enableWarningNavigation,
        setEnableWarningNavigation,
        fetchingRfpError,
        discardDraftError,
        originRfp,
        dispatchCall,
        rfp,
        setRfp,
        form: { ...rfp[FORM_INFO] },
        clientErrors: { ...rfp[CLIENT_ERROR] },
        handleSubmitRFP,
        handleSaveDraft,
        uploadRFPDocument,
        updateRFPDocument,
        deleteRFPDocument,
        isDocumentUploading,
        isSavingDraftRfp,
        handleDiscardRfpDraft,
        isDiscardDialogOpen,
        setIsDiscardDialogOpen,
        submittingError,
        handleCloseSubmitError,
        setIsDocumentsValid,
        isDocumentsValid,
      }}
    >
      {children}
    </RfpContext.Provider>
  );
}

export function useRfpContext(): RfpContextType {
  const context = useContext(RfpContext);
  if (!context) {
    throw new Error("useRfpContext must be used within RfpProvider");
  }
  return context;
}

export { RfpProvider, RfpContext };
